import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { buildFullUrl, stripLangPrefix, resolvePathToEnglish } from "@/i18n/detectLanguage";
import { usePageAlternates } from "@/contexts/PageAlternatesContext";

const languages = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { alternates } = usePageAlternates();

  const handleLanguageChange = (langCode: string) => {
    const currentLang = i18n.language;

    // Resolve a full absolute URL for the target language. Uses dedicated
    // domain (from config.domainMap) if the target has one, else main domain.
    // Handles the case where a page registered a specific alternate URL (e.g.
    // blog article with a translated slug).
    let targetUrl: string;
    if (alternates[langCode]) {
      targetUrl = alternates[langCode];
    } else {
      const basePath = stripLangPrefix(location.pathname);
      const englishPath = resolvePathToEnglish(basePath, currentLang);
      // Without a registered translation, go to the localized listing instead
      // of inventing a /language/blog/slug URL that the edge correctly 404s.
      const articleSection = englishPath.match(/^\/(blog|news)\/[^/]+\/?$/)?.[1];
      targetUrl = buildFullUrl(articleSection ? `/${articleSection}` : englishPath + location.hash, langCode);
    }

    // Cross-origin? (different domain — e.g. FR dedicated domain vs main)
    // → full page navigation so i18n, cookies, and SSR meta resolve for the
    //   correct host. Client-side navigate() would leave us on the wrong domain.
    const currentOrigin = window.location.origin;
    const target = new URL(targetUrl);
    if (target.origin !== currentOrigin) {
      window.location.href = targetUrl;
      return;
    }

    // Same origin — switch i18n and route client-side.
    i18n.changeLanguage(langCode);
    navigate(target.pathname + target.search + target.hash, { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1.5 text-sm font-body text-primary/80 hover:text-accent transition-colors outline-none">
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline">
          {languages.find((l) => l.code === i18n.language)?.flag}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`cursor-pointer ${i18n.language === lang.code ? "text-accent font-semibold" : ""}`}
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
